import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { ChatMessageRecommendations } from '../model';

type ChatMessageStore = {
  recommendationsByChat: Record<number, ChatMessageRecommendations | null>;
  isCampaignReadyByChat: Record<number, boolean>;
  isJobPreviewStaleByChat: Record<number, boolean>;

  setRecommendations: (chatId: number, recommendations: ChatMessageRecommendations | null) => void;
  clearRecommendations: (chatId: number) => void;
  setIsCampaignReady: (chatId: number, isCampaignReady: boolean) => void;
  setIsJobPreviewStale: (chatId: number, isStale: boolean) => void;
};

export const useChatMessageStore = create<ChatMessageStore>()(
  persist(
    (set) => ({
      recommendationsByChat: {},
      isCampaignReadyByChat: {},
      isJobPreviewStaleByChat: {},

      setRecommendations: (chatId, recommendations) =>
        set((state) => ({
          recommendationsByChat: {
            ...state.recommendationsByChat,
            [chatId]: recommendations,
          },
        })),
      clearRecommendations: (chatId) =>
        set((state) => ({
          recommendationsByChat: {
            ...state.recommendationsByChat,
            [chatId]: null,
          },
        })),

      setIsCampaignReady: (chatId, isCampaignReady) =>
        set((state) => ({
          isCampaignReadyByChat: {
            ...state.isCampaignReadyByChat,
            [chatId]: isCampaignReady,
          },
        })),

      setIsJobPreviewStale: (chatId, isStale) =>
        set((state) => ({
          isJobPreviewStaleByChat: {
            ...state.isJobPreviewStaleByChat,
            [chatId]: isStale,
          },
        })),
    }),
    {
      name: 'chat-message',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        return {
          isCampaignReadyByChat: state.isCampaignReadyByChat,
        };
      },
    },
  ),
);

export const useRecommendations = (chatId: number): ChatMessageRecommendations | null => {
  return useChatMessageStore((state) => state.recommendationsByChat[chatId] ?? null);
};

export const useChatMessageActions = () => {
  const setRecommendations = useChatMessageStore((state) => state.setRecommendations);
  const clearRecommendations = useChatMessageStore((state) => state.clearRecommendations);
  const setIsCampaignReady = useChatMessageStore((state) => state.setIsCampaignReady);
  const setIsJobPreviewStale = useChatMessageStore((state) => state.setIsJobPreviewStale);

  return { setRecommendations, clearRecommendations, setIsCampaignReady, setIsJobPreviewStale };
};

export const useIsCampaignReady = (chatId: number): boolean => {
  return useChatMessageStore((state) => state.isCampaignReadyByChat[chatId] ?? false);
};

export const useIsJobPreviewStale = (chatId: number): boolean => {
  return useChatMessageStore((state) => state.isJobPreviewStaleByChat[chatId] ?? false);
};
